import { MessageCallReceived } from '@wui/im'

import { useCallSessionRoomStore } from '@/entities/call-session'
import { useStoreContacts } from '@/entities/contact'
import { RTCSessionFacade } from '@/entities/webrtc'

import { useCurrentUser } from '@/shared/composables/current-user'
import { Contact } from '@/shared/types/api'
import { Room } from '@/shared/types/store'
import { fromPServedUser, getPServedFromString, isNotPServed } from '@/shared/utils/uri'

import { CallEventWithStatus, ContactType } from './types'

export type CallForwardedEvent = CallEventWithStatus & {
  event: 'call_forwarded'
  callForwardDestination: string
  callForwarder?: string
}

export const isCallForwardedEvent = (
  event: CallEventWithStatus,
): event is CallForwardedEvent =>
  event.event === 'call_forwarded' && 'callForwardDestination' in event

export const getCallForwarderPServed = (event: CallForwardedEvent): string => {
  if (!event.callForwarder) return ''
  return isNotPServed(event.callForwarder)
    ? getPServedFromString(event.callForwarder)
    : event.callForwarder
}

export const isCurrentUserCallForwardedCaller = (
  event: CallForwardedEvent,
  currentUserImLogin?: string,
): boolean => Boolean(currentUserImLogin) && event.srcPServed === currentUserImLogin

export const getCallForwardedPeerPServed = (
  event: CallForwardedEvent,
  currentUserImLogin?: string,
): string | undefined => {
  if (isCurrentUserCallForwardedCaller(event, currentUserImLogin)) {
    return getCallForwarderPServed(event) || event.pServed
  }
  return event.srcPServed || event.pServed
}

export const getCallForwardedFromLabel = (
  event: CallForwardedEvent,
  currentUserImLogin?: string,
): string => {
  if (isCurrentUserCallForwardedCaller(event, currentUserImLogin) && event.callForwarder) {
    return fromPServedUser(event.callForwarder) || event.callForwarder
  }
  if (event.srcName) return event.srcName
  if (event.srcNumber) return fromPServedUser(event.srcNumber) || event.srcNumber
  return ''
}

export const getCallForwardedToLabel = (event: CallForwardedEvent): string =>
  event.callForwardDestination || ''

export const getCallForwardedDisplayLabel = (
  event: CallForwardedEvent,
  fromLabel?: string,
  currentUserImLogin?: string,
): string => {
  const from = fromLabel || getCallForwardedFromLabel(event, currentUserImLogin)
  const to = getCallForwardedToLabel(event)
  if (from && to) return `${from} → ${to}`
  return from || to
}

export const resolveCallHistoryContactPServed = (
  event: CallEventWithStatus,
  currentUserImLogin?: string,
): string | undefined => {
  if (isCallForwardedEvent(event)) {
    return getCallForwardedPeerPServed(event, currentUserImLogin)
  }

  const { dstPServed, srcPServed, pServed } = event
  if (srcPServed && dstPServed === currentUserImLogin) {
    return srcPServed
  }
  if (dstPServed && srcPServed === currentUserImLogin) {
    return dstPServed
  }
  return dstPServed ?? srcPServed ?? pServed
}

export const getCallHistoryEventId = (event: CallEventWithStatus): number =>
  'callSourceEventId' in event && event.callSourceEventId != null ? event.callSourceEventId : event.eventId

export const getCallHistoryRank = (event: CallEventWithStatus): number =>
  'callSourceRank' in event && event.callSourceRank != null ? event.callSourceRank : event.rank

export const getCallHistoryPServed = (event: CallEventWithStatus): CallEventWithStatus['pServed'] =>
  'callSourceRemoteId' in event && event.callSourceRemoteId != null ? event.callSourceRemoteId : event.pServed

/**
 * Старые CALL_CONTACT-записи (до RTU 2.3.2-70 / 2.3.1-70a) без callSource* —
 * deep-link в peer-чат и markAsRead по fallback id/rank небезопасны (WUI-5630).
 */
export const hasCallSourceLink = (event: CallEventWithStatus): boolean =>
  'callSourceEventId' in event
  && event.callSourceEventId != null
  && 'callSourceRank' in event
  && event.callSourceRank != null

export const isOutgoingPrivateCall = (message: MessageCallReceived): boolean => {
  const { currentUser } = useCurrentUser()
  return 'srcPServed' in message && currentUser.value?.imLogin === message.srcPServed
}

export const isIncomingCall = (message: MessageCallReceived): boolean => {
  if (message.event === 'appointment_end' || message.event === 'appointment_start') {
    return true
  }
  const { currentUser } = useCurrentUser()
  return 'dstPServed' in message && currentUser.value?.imLogin === message.dstPServed
}

export const getNumberQueryByCall = (event: CallEventWithStatus): string => {
  const { currentUser } = useCurrentUser()

  let result = ''
  if (
    'dstNumber' in event &&
    event.dstNumber !== currentUser.value?.internalNumber &&
    event.dstNumber !== currentUser.value?.imLogin
  ) {
    result += event.dstNumber
  }
  if (
    'srcNumber' in event &&
    event.srcNumber !== currentUser.value?.internalNumber &&
    event.srcNumber !== currentUser.value?.imLogin
  ) {
    result += ' ' + event.srcNumber
  }
  if (isCallForwardedEvent(event) && event.callForwarder) {
    const forwarderNumber = fromPServedUser(event.callForwarder) || event.callForwarder
    if (
      forwarderNumber !== currentUser.value?.internalNumber &&
      event.callForwarder !== currentUser.value?.imLogin
    ) {
      result += ' ' + forwarderNumber
    }
  }
  return result
}
export const getNameQueryByCall = (event: CallEventWithStatus): string => {
  const { currentUser } = useCurrentUser()
  const { findByPServed } = useStoreContacts()

  let result = ''
  if ('roomName' in event) {
    result += event.roomName
  }
  if ('dstName' in event && event.dstName !== currentUser.value?.name) {
    result += ' ' + event.dstName
  }
  if ('srcName' in event && event.srcName !== currentUser.value?.name) {
    result += ' ' + event.srcName
  }
  if ('dstPServed' in event && event.dstPServed !== currentUser.value?.imLogin) {
    const contact = findByPServed(event.dstPServed)
    if (contact) {
      result += ` ${contact.name}`
    }
  }
  if ('srcPServed' in event && event.srcPServed !== currentUser.value?.imLogin) {
    const contact = findByPServed(event.srcPServed)
    if (contact) {
      result += ` ${contact.name}`
    }
  }
  return result
}

export type LiveCallSessionMatchParams = {
  number?: string
  pServed?: string
  contact?: Contact
  roomNumber?: string
}

export const findLiveCallSession = (
  sessions: RTCSessionFacade[],
  params: LiveCallSessionMatchParams,
): RTCSessionFacade | undefined => {
  const { getRoom } = useCallSessionRoomStore()

  return sessions.find((session) => {
    const room = getRoom(session.sessionId)

    if (params.pServed && room.pServedUser === params.pServed) {
      return true
    }

    if (params.roomNumber && session.number === params.roomNumber) {
      return true
    }

    if (params.number && session.number === params.number) {
      return true
    }

    if (params.contact) {
      if (session.number === params.contact.internalNumber) {
        return true
      }

      if (params.contact.imLogin && room.pServedUser === params.contact.imLogin) {
        return true
      }
    }

    return false
  })
}

export const isLiveCallInProgress = (
  sessions: RTCSessionFacade[],
  params: LiveCallSessionMatchParams,
): boolean => !!findLiveCallSession(sessions, params)

export const isDeletedConferenceCall = (
  contactType: ContactType,
  room: Room | undefined,
): boolean => {
  if (contactType !== 'room') {
    return false
  }

  if (!room) {
    return true
  }

  return room.isEnabled === false
}

export const getDeletedConferenceName = (
  item: CallEventWithStatus,
  roomId: string,
): string => {
  if ('roomName' in item && item.roomName) {
    return item.roomName
  }

  return roomId
}
